import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollToTopProps {
  /**
   * Whether to use smooth scrolling (default: true)
   */
  smooth?: boolean;
  /**
   * Custom scroll behavior - 'auto', 'smooth', or 'instant'
   */
  behavior?: ScrollBehavior;
  /**
   * Custom top position (default: 0)
   */
  top?: number;
  /**
   * Custom left position (default: 0)
   */
  left?: number;
  /**
   * Whether to scroll to top on every route change (default: true)
   */
  enabled?: boolean;
}

/**
 * ScrollToTop component that automatically scrolls to the top of the page
 * when the route changes. This ensures users start at the top of each page.
 * 
 * @param props - Configuration options for scrolling behavior
 */
const ScrollToTop: React.FC<ScrollToTopProps> = ({
  smooth = true,
  behavior = 'smooth',
  top = 0,
  left = 0,
  enabled = true
}) => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!enabled) return;

    // Use requestAnimationFrame for better performance
    const scrollToTop = () => {
      try {
        window.scrollTo({
          top,
          left,
          behavior: smooth ? behavior : 'auto'
        });
      } catch (error) {
        // Fallback for browsers that don't support smooth scrolling
        console.warn('Smooth scrolling not supported, using instant scroll');
        window.scrollTo(top, left);
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(scrollToTop, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname, smooth, behavior, top, left, enabled]);

  return null; // This component doesn't render anything
};

export default ScrollToTop; 